import { useCallback, useState } from "react";
import type { Drawing } from "./types";

interface MarkupState {
    snapshots: Drawing[][];
    index: number;
}

interface UseMarkupHistoryReturn {
    drawings: Drawing[];
    addDrawing: (drawing: Drawing) => void;
    removeDrawing: (id: string) => void;
    updateDrawingLive: (id: string, updater: (d: Drawing) => Drawing) => void;
    commitDrawingUpdate: (id: string, updater: (d: Drawing) => Drawing) => void;
    undo: () => void;
    undoPen: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    clear: () => void;
    /**
     * Reset history to a fresh single-snapshot state optionally seeded with
     * existing drawings. Used by the Phase 2 re-edit flow so the editor can
     * rehydrate with an existing record's drawings when a new `imageFile`
     * reference arrives.
     */
    reset: (seed?: Drawing[]) => void;
    /**
     * Re-map EVERY snapshot through `mapper`, leaving the cursor where it is.
     *
     * Used by rotation, which changes the coordinate space all drawings live
     * in. Mapping only the current snapshot would desync history: an Undo
     * afterwards would restore coordinates belonging to the previous
     * orientation and the annotations would jump off the photo.
     *
     * Not a history entry — rotation is a property of the photo, like crop, and
     * is reverted by the crop tool's Cancel rather than by Undo.
     */
    transformAll: (mapper: (drawings: Drawing[]) => Drawing[]) => void;
}

// Seed each pre-existing drawing as its own snapshot (empty, [d0], [d0,d1], …)
// so Undo can walk the Phase 2 re-edit flow's `initialDrawings` off one at a
// time — same model as fresh captures. Collapsing the seed into a single
// snapshot at index 0 would leave Undo permanently disabled against them.
function createInitialState(seed?: Drawing[]): MarkupState {
    if (!seed || seed.length === 0) {
        return { snapshots: [[]], index: 0 };
    }
    const snapshots: Drawing[][] = [[]];
    for (let i = 0; i < seed.length; i++) {
        snapshots.push(seed.slice(0, i + 1));
    }
    return { snapshots, index: seed.length };
}

export function useMarkupHistory(initialDrawings?: Drawing[]): UseMarkupHistoryReturn {
    const [state, setState] = useState<MarkupState>(() => createInitialState(initialDrawings));

    const drawings = state.snapshots[state.index] ?? [];

    const addDrawing = useCallback((drawing: Drawing) => {
        setState((prev) => {
            const newSnapshots = prev.snapshots.slice(0, prev.index + 1);
            const currentDrawings = newSnapshots[prev.index] ?? [];
            return {
                snapshots: [...newSnapshots, [...currentDrawings, drawing]],
                index: prev.index + 1,
            };
        });
    }, []);

    const removeDrawing = useCallback((id: string) => {
        setState((prev) => {
            const newSnapshots = prev.snapshots.slice(0, prev.index + 1);
            const currentDrawings = (newSnapshots[prev.index] ?? []).filter(
                (d) => d.id !== id
            );
            return {
                snapshots: [...newSnapshots, currentDrawings],
                index: prev.index + 1,
            };
        });
    }, []);

    // Mutate a drawing in-place without pushing a new history entry.
    // Used during active drag/pinch so every pointer-move doesn't create a history frame.
    const updateDrawingLive = useCallback((id: string, updater: (d: Drawing) => Drawing) => {
        setState((prev) => {
            const newSnapshots = [...prev.snapshots];
            const current = [...(newSnapshots[prev.index] ?? [])];
            const idx = current.findIndex((d) => d.id === id);
            if (idx !== -1) current[idx] = updater(current[idx]);
            newSnapshots[prev.index] = current;
            return { snapshots: newSnapshots, index: prev.index };
        });
    }, []);

    // Push a new history snapshot with the updated drawing.
    // Called on gesture end so undo reverts to the pre-gesture state.
    const commitDrawingUpdate = useCallback((id: string, updater: (d: Drawing) => Drawing) => {
        setState((prev) => {
            const sliced = prev.snapshots.slice(0, prev.index + 1);
            const current = [...(sliced[prev.index] ?? [])];
            const idx = current.findIndex((d) => d.id === id);
            if (idx !== -1) current[idx] = updater(current[idx]);
            return {
                snapshots: [...sliced, current],
                index: prev.index + 1,
            };
        });
    }, []);

    const undo = useCallback(() => {
        setState((prev) => {
            if (prev.index <= 0) return prev;
            return { ...prev, index: prev.index - 1 };
        });
    }, []);

    // Undo only pen strokes — skips snapshots that only differ by text drawings
    const undoPen = useCallback(() => {
        setState((prev) => {
            const currentPenIds = new Set(
                (prev.snapshots[prev.index] ?? [])
                    .filter((d) => d.type === "pen")
                    .map((d) => d.id)
            );
            // Walk backwards to find a snapshot with fewer pen drawings
            for (let i = prev.index - 1; i >= 0; i--) {
                const snapshotPenIds = new Set(
                    (prev.snapshots[i] ?? [])
                        .filter((d) => d.type === "pen")
                        .map((d) => d.id)
                );
                if (snapshotPenIds.size < currentPenIds.size) {
                    return { ...prev, index: i };
                }
            }
            return prev;
        });
    }, []);

    const redo = useCallback(() => {
        setState((prev) => {
            if (prev.index >= prev.snapshots.length - 1) return prev;
            return { ...prev, index: prev.index + 1 };
        });
    }, []);

    const clear = useCallback(() => {
        setState({ snapshots: [[]], index: 0 });
    }, []);

    const reset = useCallback((seed?: Drawing[]) => {
        setState(createInitialState(seed));
    }, []);

    const transformAll = useCallback((mapper: (drawings: Drawing[]) => Drawing[]) => {
        setState((prev) => ({
            snapshots: prev.snapshots.map(mapper),
            index: prev.index,
        }));
    }, []);

    return {
        drawings,
        addDrawing,
        removeDrawing,
        updateDrawingLive,
        commitDrawingUpdate,
        undo,
        undoPen,
        redo,
        canUndo: state.index > 0,
        canRedo: state.index < state.snapshots.length - 1,
        clear,
        reset,
        transformAll,
    };
}
